// Pointer-event gesture recognizers for map markers.
//
// We roll our own tap/drag detection rather than relying on `click` because
// Leaflet's pan/pinch handling, combined with sub-pixel pointer jitter, makes
// the synthesized `click` event unreliable on touch devices. Pointer events
// (vs mouse events) are a hard requirement for touch + stylus to work.

export const TAP_MAX_MOVE_PX = 12;
export const TAP_MAX_DURATION_MS = 500;

export interface TapEvalArgs {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startTime: number;
  endTime: number;
}

/**
 * Pure predicate: did a pointer-up at (endX, endY, endTime) qualify as a tap
 * given the pointer-down at (startX, startY, startTime)? Pulled out for unit
 * testing — the DOM-level recognizer below delegates here.
 */
export function isTap({
  startX,
  startY,
  endX,
  endY,
  startTime,
  endTime,
}: TapEvalArgs): boolean {
  const dx = endX - startX;
  const dy = endY - startY;
  const close = Math.hypot(dx, dy) <= TAP_MAX_MOVE_PX;
  const quick = endTime - startTime <= TAP_MAX_DURATION_MS;
  return close && quick;
}

/**
 * Fires `onTap` when the user taps the element.
 *
 * Two independent paths race to fire, whichever succeeds first wins:
 *   1. Native `click` — most reliable on modern mobile browsers when
 *      `touch-action: manipulation` lets the browser synthesize it.
 *   2. Pointer-based recognizer — fallback for environments where click
 *      is swallowed (some iOS + Leaflet interactions we've seen).
 *
 * A short dedupe window suppresses the second path if the first already
 * fired for the same gesture.
 *
 * Returns a teardown that removes both listeners.
 */
export function attachTapDetector(
  el: HTMLElement,
  onTap: () => void,
): () => void {
  let lastFired = 0;
  const fire = () => {
    const now = Date.now();
    if (now - lastFired < 400) return;
    lastFired = now;
    onTap();
  };

  // Primary: native click. Works reliably on desktop mouse and on
  // touch-action: manipulation elements on mobile.
  function onClick(e: MouseEvent) {
    if (e.button !== undefined && e.button !== 0) return;
    fire();
  }

  // Fallback: pointer-based recognizer. Tolerates small finger jitter
  // (<= TAP_MAX_MOVE_PX) and only fires on quick releases.
  function onDown(e: PointerEvent) {
    if (!e.isPrimary) return;
    if (e.button !== undefined && e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startTime = Date.now();
    const pointerId = e.pointerId;
    let settled = false;

    function settle(shouldFire: boolean) {
      if (settled) return;
      settled = true;
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
      document.removeEventListener('pointermove', onMove);
      if (shouldFire) fire();
    }
    function onUp(up: PointerEvent) {
      if (up.pointerId !== pointerId) return;
      const shouldFire = isTap({
        startX,
        startY,
        endX: up.clientX,
        endY: up.clientY,
        startTime,
        endTime: Date.now(),
      });
      settle(shouldFire);
    }
    function onMove(mv: PointerEvent) {
      if (mv.pointerId !== pointerId) return;
      const dx = mv.clientX - startX;
      const dy = mv.clientY - startY;
      if (Math.hypot(dx, dy) > TAP_MAX_MOVE_PX) settle(false);
    }
    function onCancel(c: PointerEvent) {
      if (c.pointerId !== pointerId) return;
      settle(false);
    }

    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    document.addEventListener('pointermove', onMove);
  }

  el.addEventListener('click', onClick);
  el.addEventListener('pointerdown', onDown);
  return () => {
    el.removeEventListener('click', onClick);
    el.removeEventListener('pointerdown', onDown);
  };
}
